import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { FUNNEL_REPORTS_TEST_IDS } from '../testIds';
import type { MetricQualification } from '../types';
import { StatTierChip } from './StatTierChip';

/**
 * B1 (`.design/studio-reports/IMPLEMENTATION_SPEC.md`): the Funnel gates band —
 * ONE card that replaces the «Project overview» card AND the KPI grid below it.
 * It is the canonical gate contract of `modules/funnel-insights/MODULE.md:379`:
 * Starts, First → second, Paywall reached, Paywall to buy, Purchase conv
 * (purchases ÷ ALL starts), then Revenue and the data-quality chip under a rule.
 *
 * It renders what it is given and computes nothing. Every number, rate,
 * denominator and typed absence is resolved by the dashboard from the SERVED
 * payload — the band must never be able to divide, sum or fabricate a figure
 * (AC-056: no client-side people arithmetic).
 */

/** The unit noun rides the VISIBLE label after a middot (designer.md D5). */
const UNIT_SEPARATOR = '· ';

export interface FunnelGateCellModel {
  /** Reused KPI id — `funnel-reports-kpi-<id>` (the deleted tiles' test ids). */
  id: string;
  label: string;
  /** `people` / `visits`; a gate with no person twin keeps its own noun. */
  unitNoun: string;
  /** Already formatted: a rate, a count, or the bare `n/a` of a typed absence. */
  value: string;
  /**
   * `n / d` or a short phrase. B3: a typed-absence cell carries NO evidence
   * line — the reason is stated once for the band instead.
   */
  evidence?: string;
  /**
   * Relative change against the RAW prior-period summary (PE6-AC-006). `null`
   * or absent renders nothing — never a placeholder (B1).
   */
  change?: number | null;
  /** Shown in the change row's slot when this gate has no prior twin at all. */
  changeNote?: string;
  qualification?: MetricQualification;
  /** Mutes the value and drops the tier chip — a typed absence, not a zero. */
  unavailable?: boolean;
}

export interface FunnelGatesBandProps {
  gates: FunnelGateCellModel[];
  /** The window the band is read in, e.g. `May 18 – May 24`. */
  windowLabel?: string;
  /** `vs prior period (<dates>)` — stated ONCE, not per cell. */
  comparisonLabel?: string;
  /** Why the band shows no change rows, when that needs saying. */
  comparisonAbsenceLabel?: string;
  revenue: Pick<FunnelGateCellModel, 'label' | 'unitNoun' | 'value' | 'evidence'>;
  /** Rendered by the dashboard — the band does not own the severity vocabulary. */
  qualityChip: React.ReactNode;
  /** R4-AC-002: the quality line LEADS with the minimum daily health score. */
  qualityScore: string;
  /**
   * B3: stated once for the whole band when the scope serves no person twins,
   * with a display-only «Show visits» action. No cell repeats the reason.
   */
  absence?: { message: string; onShowVisits?: () => void };
  /**
   * FT-AC-111 / AC-019: people are not additive across placements, and the
   * reader who adds the rows up must find that in VISIBLE text, never a tooltip.
   */
  nonAdditivityCaption?: string;
}

function GateCell({ gate, index }: { gate: FunnelGateCellModel; index: number }): React.ReactElement {
  const showChange = typeof gate.change === 'number' && Number.isFinite(gate.change);
  return (
    <Box
      data-testid={FUNNEL_REPORTS_TEST_IDS.kpi(gate.id)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        minHeight: 92,
        px: { xs: 0, lg: 2.25 },
        // The rule between cells reads as one band, not five boxes. It is
        // dropped with the grid at <=1200px, where cells stack into rows.
        borderLeft: theme => ({ xs: 0, lg: index === 0 ? 0 : `1px solid ${theme.palette.divider}` }),
        '&:first-of-type': { pl: 0 },
      }}
    >
      {/* The unit noun is NOT faded. `opacity: 0.6` over `text.secondary`
          composited to 2.85:1 (light) / 3.78:1 (dark) — below the 4.5:1 bar,
          measured on the real render. It is the text that says whether the
          number counts people or visits, which is the last thing on this band
          that may be hard to read. It stays `text.secondary` (7.5:1) and is
          told apart from the label by the separator alone.
          The label WRAPS: at 1200 px the five equal tracks are ~191 px and
          «Converted (trial or purchase) · people» is 231 px, so `nowrap` pushed
          it through the card border. */}
      <Typography variant="body2" color="text.secondary">
        {gate.label}
        {' '}
        <Box component="span">{`${UNIT_SEPARATOR}${gate.unitNoun}`}</Box>
      </Typography>
      {/* The approved mock drew these at 1.75rem / 1.375rem. Neither is on the
          DESIGN.md type ramp (2.125 / 1.5 / 1.25 / 1 / 0.875 / 0.75), so the
          band uses the h5 and h6 steps instead: the hierarchy the mock asked
          for — gate value above revenue above evidence — with no new step. */}
      <Typography
        variant={gate.unavailable ? 'h6' : 'h5'}
        // A11y: `h5`/`h6` are SIZES here. Rendered as heading elements they put
        // «7,167» and «25.5%» into the document outline next to real section
        // titles, so a screen-reader heading list read as a column of numbers.
        component="div"
        sx={{
          lineHeight: 1.15,
          fontWeight: 500,
          fontVariantNumeric: 'tabular-nums',
          color: gate.unavailable ? 'text.secondary' : 'text.primary',
        }}
      >
        {gate.value}
      </Typography>
      {gate.evidence && (
        <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {gate.evidence}
        </Typography>
      )}
      {showChange ? (
        <Typography
          data-testid={FUNNEL_REPORTS_TEST_IDS.kpiDelta(gate.id)}
          variant="caption"
          sx={{ color: (gate.change as number) >= 0 ? 'success.main' : 'error.main' }}
        >
          {`${(gate.change as number) >= 0 ? '▲' : '▼'} ${formatChange(gate.change as number)}`}
        </Typography>
      ) : gate.changeNote ? (
        <Typography variant="caption" color="text.secondary">{gate.changeNote}</Typography>
      ) : null}
      {!gate.unavailable && gate.qualification && (
        <Box sx={{ mt: 0.25 }}>
          {/* B2 / AC-013: the band prints the rate itself one line above, so the
              chip carries the VERDICT only. Its popover still holds n, the
              interval and the method. */}
          <StatTierChip qualification={gate.qualification} label="verdict" />
        </Box>
      )}
    </Box>
  );
}

function formatChange(change: number): string {
  return `${Math.abs(change * 100).toFixed(1)}%`;
}

export function FunnelGatesBand({
  gates,
  windowLabel,
  comparisonLabel,
  comparisonAbsenceLabel,
  revenue,
  qualityChip,
  qualityScore,
  absence,
  nonAdditivityCaption,
}: FunnelGatesBandProps): React.ReactElement {
  const meta = [windowLabel, comparisonLabel ?? comparisonAbsenceLabel, 'each rate keeps its numerator and denominator']
    .filter(Boolean)
    .join(' · ');

  return (
    <Box
      data-testid={FUNNEL_REPORTS_TEST_IDS.projectOverview}
      sx={{
        border: theme => `1px solid ${theme.palette.divider}`,
        borderRadius: 4,
        px: 3,
        py: 2.5,
        bgcolor: 'background.paper',
      }}
    >
      <Stack
        direction="row"
        alignItems="baseline"
        justifyContent="space-between"
        spacing={2}
        useFlexGap
        flexWrap="wrap"
        sx={{ mb: 1.75 }}
      >
        {/* A11y: `subtitle1` renders an <h6> by default, which skipped two levels
            under the page's <h4> title. The band is the first block on the
            page, so its heading is h5. */}
        <Typography variant="subtitle1" component="h5" sx={{ fontWeight: 500 }}>Funnel gates</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'right' }}>{meta}</Typography>
      </Stack>

      {/* 5 / 2 / 1 columns at 1200 / 720 px (B1). */}
      <Box
        sx={{
          display: 'grid',
          // `minmax(0, 1fr)`, not `1fr`: a bare `1fr` is `minmax(auto, 1fr)`, so a
          // long label grew its own track — measured 108 / 184 / 191 / 170 /
          // 268 px at 1280 px instead of five equal columns, and the fifth cell
          // overflowed the card by 20 px at 1280 and 99 px at 1200.
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(5, minmax(0, 1fr))' },
          columnGap: { xs: 0, sm: 3, lg: 0 },
          rowGap: { xs: 2, lg: 0 },
          alignItems: 'stretch',
        }}
      >
        {gates.map((gate, index) => <GateCell key={gate.id} gate={gate} index={index} />)}
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={3}
        useFlexGap
        flexWrap="wrap"
        sx={{ mt: 2, pt: 1.75, borderTop: theme => `1px solid ${theme.palette.divider}` }}
      >
        <Stack direction="row" alignItems="baseline" spacing={1.5} useFlexGap flexWrap="wrap">
          <Typography variant="body2" color="text.secondary">
            {revenue.label}
            {' '}
            <Box component="span">{`${UNIT_SEPARATOR}${revenue.unitNoun}`}</Box>
          </Typography>
          <Typography
            data-testid={FUNNEL_REPORTS_TEST_IDS.kpi('revenue')}
            variant="h6"
            sx={{ fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}
          >
            {revenue.value}
          </Typography>
          {revenue.evidence && (
            <Typography variant="body2" color="text.secondary">{revenue.evidence}</Typography>
          )}
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1} data-testid={FUNNEL_REPORTS_TEST_IDS.kpi('quality')}>
          <Typography variant="body2" color="text.secondary">Data quality</Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
            {qualityScore}
          </Typography>
          <Box data-testid={FUNNEL_REPORTS_TEST_IDS.kpiSeverity('quality')}>{qualityChip}</Box>
        </Stack>
      </Stack>

      {absence && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          useFlexGap
          flexWrap="wrap"
          data-testid={FUNNEL_REPORTS_TEST_IDS.projectOverviewStat('people-absence')}
          sx={{
            mt: 2,
            px: 2,
            py: 1.5,
            border: theme => `1px solid ${theme.palette.divider}`,
            borderRadius: 3,
            bgcolor: 'action.hover',
          }}
        >
          <Typography variant="body2" color="text.secondary">{absence.message}</Typography>
          {absence.onShowVisits && (
            <Button
              size="small"
              variant="outlined"
              data-testid={FUNNEL_REPORTS_TEST_IDS.projectOverviewShowVisits}
              onClick={absence.onShowVisits}
              sx={{ flex: 'none' }}
            >
              Show visits
            </Button>
          )}
        </Stack>
      )}

      {nonAdditivityCaption && (
        <Typography
          variant="caption"
          color="text.secondary"
          data-testid={FUNNEL_REPORTS_TEST_IDS.peopleScopeCaption}
          sx={{ display: 'block', mt: 1.5 }}
        >
          {nonAdditivityCaption}
        </Typography>
      )}
    </Box>
  );
}
